/**
 * Page-load event policy — decides which of the three per-page-load PostHog events
 * (`rw_posthog_initialized`, `$pageview`, `$pageleave`) the widget emits.
 *
 * Those three fire on every page load, whether or not the widget can appear, and are
 * ~90% of ingested volume. Most of that is on pages the client's own config says Rose
 * must never show on. Suppressing them there is pure cost with no data loss, provided
 * two things keep working:
 *
 * - posthog-js still initializes, so CTA-click and form-submit tracking keeps a
 *   `person_id`. Only these three events are suppressed, never the SDK.
 * - `$pageview` and `$pageleave` survive wherever they carry meaning. `$pageview` is
 *   the anchor the batch processor uses to create a `visitor_sessions` row and feeds
 *   page journeys and the bounce definition; `$pageleave` is the sole input to the
 *   session-duration estimator, and duration is what the backoffice per-visitor panels
 *   report. Both are dropped only on hosts that appear nowhere in `display_only_on_urls`
 *   (a logged-in product app pulled in by the config resolver's root-domain fallback) —
 *   never on a page that is merely hidden on an in-scope marketing site.
 *
 * Note the asymmetry: the two page events key off `hostInScope` (host-level) while
 * `rw_posthog_initialized` keys off `urlEligible` (page-level). That is the whole point.
 */

import type { AnalyticsConfig as GeneratedAnalyticsConfig } from '../types/configs.generated';

export type PageLoadEventSuppressionMode = NonNullable<
    GeneratedAnalyticsConfig['page_load_event_suppression']
>;

export interface PageLoadEventPolicyInput {
    /** Result of isUrlAllowed() — is the widget allowed to display on THIS page. */
    urlEligible: boolean;
    /** Result of isHostInAllowlistScope() — is this host in the allowlist AT ALL. */
    hostInScope: boolean;
    /**
     * Is this page one Rose deliberately watches without displaying — a CTA
     * destination, a form_tracking_pages match, or a thank_you_pages match.
     * Such a page keeps its `$pageview` so the conversion it captures still has a
     * session to hang off.
     */
    isTrackedSurface: boolean;
    mode: PageLoadEventSuppressionMode;
}

export interface PageLoadEventPolicy {
    /** Passed to posthog.init as capture_pageview (also gates $pageleave upstream). */
    capturePageview: boolean;
    /** Passed to posthog.init as capture_pageleave. */
    capturePageleave: boolean;
    /** Whether DisplayController should emit rw_posthog_initialized. */
    trackInitEvent: boolean;
}

export function resolvePageLoadEventPolicy({
    urlEligible,
    hostInScope,
    isTrackedSurface,
    mode,
}: PageLoadEventPolicyInput): PageLoadEventPolicy {
    if (mode === 'off' || urlEligible) {
        return { capturePageview: true, capturePageleave: true, trackInitEvent: true };
    }

    // The page is blocked. rw_posthog_initialized always goes: the experiment mart
    // already discards url_allowed=false anchors.
    //
    // $pageleave follows $pageview rather than the init event. It is not a refinement
    // on the duration estimate — it IS the estimate: compute_session_duration_seconds
    // prefers the last $pageleave as the session end, and its capped-gap-sum fallback
    // returns 0 once the stream drops under two events, which is exactly what a blocked
    // page becomes when init goes too. IX-4183 measured zero-duration sessions going
    // 17% -> 78% on lemlist over the three days it was suppressed.
    //
    // What that costs is the raw duration signal, not a client-facing rate: the
    // engagement denominator (mv_widget_visitors) only counts sessions carrying a
    // widget_impression, and impressions fire only where urlEligible is true — the one
    // branch this function never suppresses. Measured denominators stayed flat. The
    // real consumers of the degraded column are visitor_sessions.session_duration_seconds
    // / is_engaged, the backoffice per-visitor duration panels (which read it with no
    // impression filter), and analytics.experiment_sessions.duration_sec.
    const dropPageEvents = mode === 'out_of_scope_hosts' && !hostInScope && !isTrackedSurface;
    const capturePageview = !dropPageEvents;

    return {
        capturePageview,
        // 'init_and_pageleave' keeps the literal behaviour its name describes: it drops
        // $pageleave on every blocked page, and with it the duration signal on any host
        // that uses URL targeting. A deliberate cost-over-measurement lever, not a
        // rollback step for this failure.
        capturePageleave: mode === 'init_and_pageleave' ? false : capturePageview,
        trackInitEvent: false,
    };
}
